export const CprMetronomeSettings = {
  MIN_BPM: 100,
  MAX_BPM: 120,
  DEFAULT_BPM: 110,
  AUDIO_FREQUENCY_HZ: 880, // A5 tone for medical metronome clarity
  PULSE_DURATION_MS: 50
} as const;

export enum ClinicalAidType {
  CPR_ADULT = 'CPR_ADULT',
  CPR_CHILD = 'CPR_CHILD',
  CPR_INFANT = 'CPR_INFANT',
  HEIMLICH_ADULT = 'HEIMLICH_ADULT',
  HEIMLICH_CHILD = 'HEIMLICH_CHILD',
  RECOVERY_POSITION = 'RECOVERY_POSITION',
  SEVERE_BLEEDING_TOURNIQUET = 'SEVERE_BLEEDING_TOURNIQUET'
}

export interface ClinicalAidCatalogItem {
  id: ClinicalAidType;
  title: string;
  category: 'RESUSCITATION' | 'CHOKING' | 'TRAUMA' | 'FIRST_AID';
  shortInstructions: string[];
  recommendedBpm?: number;
  iconName: string;
}

export const CLINICAL_AID_CATALOG: Record<ClinicalAidType, ClinicalAidCatalogItem> = {
  [ClinicalAidType.CPR_ADULT]: {
    id: ClinicalAidType.CPR_ADULT,
    title: 'RCP en Adulto',
    category: 'RESUSCITATION',
    shortInstructions: [
      'Coloque a la víctima boca arriba en superficie firme.',
      'Coloque el talón de una mano en el centro del pecho y la otra encima.',
      'Comprima fuerte y rápido (5 a 6 cm de profundidad) a 100-120 lpm.',
      'Permita el retroceso completo del pecho tras cada compresión.'
    ],
    recommendedBpm: 110,
    iconName: 'heart-pulse'
  },
  [ClinicalAidType.CPR_CHILD]: {
    id: ClinicalAidType.CPR_CHILD,
    title: 'RCP en Niño (1 a 8 años)',
    category: 'RESUSCITATION',
    shortInstructions: [
      'Use 1 o 2 manos según el tamaño del niño.',
      'Comprima un tercio del diámetro del pecho (aprox 5 cm).',
      'Ritmo continuo a 100-120 lpm.'
    ],
    recommendedBpm: 110,
    iconName: 'child'
  },
  [ClinicalAidType.CPR_INFANT]: {
    id: ClinicalAidType.CPR_INFANT,
    title: 'RCP en Lactante (< 1 año)',
    category: 'RESUSCITATION',
    shortInstructions: [
      'Coloque al lactante en superficie plana y firme.',
      'Use dos dedos en el centro del tórax justo debajo de la línea de pezones.',
      'Comprima unos 4 cm a 100-120 lpm sin presionar el abdomen.'
    ],
    recommendedBpm: 110,
    iconName: 'baby'
  },
  [ClinicalAidType.HEIMLICH_ADULT]: {
    id: ClinicalAidType.HEIMLICH_ADULT,
    title: 'Maniobra de Heimlich (Adulto/Niño consciente)',
    category: 'CHOKING',
    shortInstructions: [
      'Póngase de pie detrás de la persona y rodee su cintura con sus brazos.',
      'Forme un puño con una mano y colóquela justo arriba del ombligo.',
      'Sujete el puño con la otra mano.',
      'Presione con fuerza hacia adentro y hacia arriba de forma rápida y seca.'
    ],
    iconName: 'air'
  },
  [ClinicalAidType.HEIMLICH_CHILD]: {
    id: ClinicalAidType.HEIMLICH_CHILD,
    title: 'Atragantamiento Lactante (< 1 año)',
    category: 'CHOKING',
    shortInstructions: [
      'Coloque al bebé boca abajo sobre su antebrazo, sosteniendo su cabeza con la mano.',
      'Dé hasta 5 golpes firmes entre los omóplatos con el talón de su mano.',
      'Si no desobstruye, gire al bebé boca arriba y dé 5 compresiones torácicas.'
    ],
    iconName: 'baby-choking'
  },
  [ClinicalAidType.RECOVERY_POSITION]: {
    id: ClinicalAidType.RECOVERY_POSITION,
    title: 'Posición Lateral de Seguridad',
    category: 'FIRST_AID',
    shortInstructions: [
      'Si la persona respira pero no responde, colóquela de lado.',
      'Flexione la pierna superior en ángulo recto para estabilizar el cuerpo.',
      'Incline suavemente la cabeza hacia atrás para mantener la vía aérea permeable.'
    ],
    iconName: 'bed'
  },
  [ClinicalAidType.SEVERE_BLEEDING_TOURNIQUET]: {
    id: ClinicalAidType.SEVERE_BLEEDING_TOURNIQUET,
    title: 'Control de Hemorragia Severa',
    category: 'TRAUMA',
    shortInstructions: [
      'Aplique presión directa y firme sobre la herida con tela limpia.',
      'Si la hemorragia es exanguinante en extremidad, aplique torniquete 5-7 cm arriba de la herida.',
      'Ajuste hasta que cese el sangrado rojo brillante. Anote la hora exacta.'
    ],
    iconName: 'band-aid'
  }
};
